import React, { useEffect, useRef, useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { engagementsApi, type EngagementSummary } from '@/lib/engagementsApi'

interface ContextMenuTarget {
  engagement: Pick<EngagementSummary, 'id' | 'name' | 'client_name' | 'scope'> & {
    notes?: string | null
  }
  x: number
  y: number
}

interface Props {
  target:      ContextMenuTarget | null
  onClose:     () => void
  onUpdated:   () => void
  onDeleted:   () => void
  onError?:    (msg: string) => void
}

export const EngagementContextMenu: React.FC<Props> = ({
  target, onClose, onUpdated, onDeleted, onError,
}) => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [mode, setMode] = useState<'menu' | 'edit' | 'confirm-delete'>('menu')
  const [busy, setBusy] = useState(false)

  const [name,        setName]       = useState('')
  const [clientName,  setClientName] = useState('')
  const [scope,       setScope]      = useState('')
  const [notes,       setNotes]      = useState('')

  useEffect(() => {
    if (!target) return
    setMode('menu')
    setName(target.engagement.name ?? '')
    setClientName(target.engagement.client_name ?? '')
    setScope(target.engagement.scope ?? '')
    setNotes(target.engagement.notes ?? '')
  }, [target])

  useEffect(() => {
    if (!target) return
    const handleDown = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) onClose()
    }
    const handleKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onClose() }
    document.addEventListener('mousedown', handleDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [target, onClose])

  if (!target) return null

  const clamped = (() => {
    const w = mode === 'menu' ? 200 : 380
    const h = mode === 'menu' ? 96  : (mode === 'confirm-delete' ? 160 : 360)
    let left = target.x
    let top  = target.y
    if (typeof window !== 'undefined') {
      if (left + w > window.innerWidth  - 8) left = window.innerWidth  - w - 8
      if (top  + h > window.innerHeight - 8) top  = window.innerHeight - h - 8
    }
    return { left: Math.max(8, left), top: Math.max(8, top) }
  })()

  async function handleSave() {
    if (!target) return
    if (!name.trim()) {
      onError?.(t('pentest.contextMenu.nameRequired', 'Name is required') as string)
      return
    }
    setBusy(true)
    try {
      await engagementsApi.update(target.engagement.id, {
        name:        name.trim(),
        client_name: clientName.trim() ? clientName.trim() : null,
        scope:       scope.trim() ? scope.trim() : null,
        notes:       notes.trim() ? notes.trim() : null,
      })
      onUpdated()
      onClose()
    } catch (err: any) {
      onError?.(err?.message || (t('pentest.contextMenu.updateFailed', 'Update failed') as string))
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!target) return
    setBusy(true)
    try {
      await engagementsApi.delete(target.engagement.id)
      onDeleted()
      onClose()
    } catch (err: any) {
      onError?.(err?.message || (t('pentest.contextMenu.deleteFailed', 'Delete failed') as string))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 rounded-md border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
      style={{ left: clamped.left, top: clamped.top }}
      onContextMenu={e => e.preventDefault()}
    >
      {mode === 'menu' && (
        <ul className="w-48 py-1 text-xs">
          <li>
            <button
              type="button"
              onClick={() => setMode('edit')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('pentest.contextMenu.edit', 'Edit details')}
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => setMode('confirm-delete')}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('pentest.contextMenu.delete', 'Delete engagement')}
            </button>
          </li>
        </ul>
      )}

      {mode === 'edit' && (
        <div className="w-[380px] p-3 text-xs">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-400">
            {t('pentest.contextMenu.editTitle', 'Edit engagement')}
          </p>
          <label className="mb-2 block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              {t('pentest.contextMenu.field.name', 'Name')}
            </span>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </label>
          <label className="mb-2 block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              {t('pentest.contextMenu.field.client', 'Client')}
            </span>
            <input
              type="text"
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </label>
          <label className="mb-2 block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              {t('pentest.contextMenu.field.scope', 'Scope')}
            </span>
            <input
              type="text"
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </label>
          <label className="mb-2 block">
            <span className="mb-0.5 block text-[10px] text-slate-500">
              {t('pentest.contextMenu.field.notes', 'Notes')}
            </span>
            <textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full resize-none rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={handleSave}
              className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy
                ? t('common.saving', 'Saving…')
                : t('common.save', 'Save')}
            </button>
          </div>
        </div>
      )}

      {mode === 'confirm-delete' && (
        <div className="w-[380px] p-3 text-xs">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-red-500">
            {t('pentest.contextMenu.deleteTitle', 'Delete engagement?')}
          </p>
          <p className="mb-3 text-slate-700 dark:text-slate-200">
            {t('pentest.contextMenu.deleteWarning',
               'This permanently removes the engagement and all of its assets, ports, and attack runs. This cannot be undone.')}
          </p>
          <p className="mb-3 truncate rounded bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 dark:bg-slate-900/50 dark:text-slate-200">
            {target.engagement.name}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode('menu')}
              className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={handleDelete}
              className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy
                ? t('common.deleting', 'Deleting…')
                : t('common.delete', 'Delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export type { ContextMenuTarget as EngagementContextMenuTarget }
